<template>
  <div>
    <transition mode="out-in">
      <div v-if="fetching" class="h-page d-flex align-items-center justify-content-center">
        <loading-spinner />
      </div>
      <div v-else-if="error" class="alert alert-warning">
        {{ error }}
      </div>
      <div v-else class="h-page order-container">

        <div class="row mb-3">
          <div class="col-12 col-lg-6 order-0 order-lg-0 lg-3 mb-3">

            <div v-if="!order.lineItems || !order.lineItems.length">
              <div class="order-item-name lead fw-bold overflow-auto" style="height:70px">
                {{ order.item_name }}
              </div>

              <div class="mb-3">
                <strong v-if="order.original_price" class="display-6 text-muted me-3">
                  <strike>${{ parseFloat( order.original_price ).toFixed(2) }}</strike>
                </strong>
                <strong class="display-6 text-success">${{ parseFloat( order.sold_for || 0 ).toFixed(2) }}</strong>
              </div>

              <order-gallery :order-img="order.photo_url" :remote-id="order.remote_id" />

            </div>
            <div v-else>
              <local-line-item
                  v-for="(item, index) in order.lineItems"
                  :key="item.remote_id || index"
                  :item="item"
                  :discreet="discreet"
                  class="mb-3"
                  @update:item="(updated) => updateLineItem(index, updated)"
              />
            </div>
          </div>

          <div class="col-12 col-lg-6 order-1 order-lg-1 mb-3 mb-lg-0">


            <div v-if="actionError && !showCancelModal && !showReturnModal && !showHideModal" class="alert alert-danger alert-dismissible mb-3">
              {{ actionError }}
              <button type="button" class="btn-close" @click="actionError = null"></button>
            </div>

            <div class="card">
              <div class="card-body">
                <div>
                  <span
                    v-if="order.order_status==='Cancelled'"
                    class="badge bg-danger"
                    title="This order is hidden from reports and insights">
                      <i class="bi bi-ban me-1"></i>
                      Cancelled
                    </span>
                  <span
                      v-if="order.order_status==='Returned'"
                      class="badge bg-warning"
                      title="Returned">
                      <i class="bi bi-arrow-return-left me-1"></i>
                      Returned
                    </span>
                </div>
                <div class="d-flex align-items-center mb-3">
                  <platform-icon :platform="order.remote_store" class="me-2" />
                  <h2 class="m-0">
                    {{ order.remote_store }}
                    Order</h2>
                  <!-- Origin Badge with Actions Dropdown -->
                  <cross-listing-gate>
                  <div v-if="!discreet" class="ms-auto d-flex align-items-center gap-2">
                    <span
                      v-if="order.is_hidden"
                      class="badge bg-dark"
                      title="This order is hidden from reports and insights">
                      <i class="bi bi-eye-slash me-1"></i>
                      Hidden
                    </span>
                    <span
                      v-if="order.is_system"
                      class="badge bg-secondary"
                      title="Automatically imported from platform API">
                      <i class="bi bi-robot me-1"></i>
                      Platform Managed
                    </span>
                    <span v-else
                      class="badge bg-primary"
                      title="Manually entered by user">
                      <i class="bi bi-pencil me-1"></i>
                      Manual Entry
                    </span>
                    <!-- Actions Dropdown (All Orders) -->
                    <div class="dropdown">
                      <button
                        class="btn btn-sm btn-light border border-dark rounded rounded-pill text-muted p-0"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        title="Order actions"
                        style="width: 24px; height: 24px;">
                        <i class="bi bi-three-dots"></i>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end">
                        <!-- Hidden orders only get Unhide -->
                        <template v-if="order.is_hidden">
                          <li>
                            <button
                              class="dropdown-item"
                              @click="showHideModal = true"
                              :disabled="actionInProgress">
                              <i class="bi bi-eye me-2 text-success"></i>
                              Unhide Order
                            </button>
                          </li>
                        </template>
                        <!-- Normal (non-hidden) order actions -->
                        <template v-else>
                          <template v-if="!order.is_system && order.order_status === 'Completed'">
                            <li>
                              <button
                                class="dropdown-item"
                                @click="showCancelModal = true"
                                :disabled="actionInProgress">
                                <i class="bi bi-x-circle me-2 text-warning"></i>
                                Cancel Order
                              </button>
                            </li>
                            <li>
                              <button
                                class="dropdown-item"
                                @click="showReturnModal = true"
                                :disabled="actionInProgress">
                                <i class="bi bi-currency-dollar me-2 text-info"></i>
                                Refund Order
                              </button>
                            </li>
                            <li>
                              <button
                                class="dropdown-item"
                                @click="emit('edit', order)"
                                :disabled="actionInProgress">
                                <i class="bi bi-pencil-square me-2"></i>
                                Edit Order
                              </button>
                            </li>
                            <li><hr class="dropdown-divider"></li>
                          </template>
                          <template v-if="!order.is_system && (order.order_status === 'Cancelled' || order.order_status === 'Returned')">
                            <li>
                              <button
                                class="dropdown-item"
                                @click="handleUndoCancel"
                                :disabled="actionInProgress">
                                <span v-if="actionInProgress" class="spinner-border spinner-border-sm me-2"></span>
                                <i v-else class="bi bi-arrow-counterclockwise me-2 text-success"></i>
                                Undo {{ order.order_status === 'Cancelled' ? 'Cancellation' : 'Return' }}
                              </button>
                            </li>
                            <li><hr class="dropdown-divider"></li>
                          </template>
                          <li>
                            <button
                              class="dropdown-item"
                              @click="showHideModal = true"
                              :disabled="actionInProgress">
                              <i class="bi bi-eye-slash me-2 text-muted"></i>
                              Hide Order
                            </button>
                          </li>
                        </template>
                      </ul>
                    </div>
                  </div>
                  </cross-listing-gate>
                </div>

                <div v-if="(order.buyer_username || order.ship_city || order.ship_state)" class="card mb-3">
                  <div class="card-body d-flex align-items-center">
                    <div  class="me-2">
                      <div class="text-muted small text-uppercase fw-semibold">
                        <i class="bi bi-truck text-success me-1"></i>
                        Ship To
                      </div>
                    </div>
                    <div class="ms-auto" :title="[order.ship_name, order.buyer_username, order.ship_street1, order.ship_street2, order.ship_city, order.ship_state, order.ship_zip, order.ship_country].filter(Boolean).join(', ')">{{ [order.ship_city, order.ship_state, order.ship_country].filter(Boolean).join(', ') }}</div>
                  </div>
                </div>

                <div class="">
                  <div class="d-flex">
                    <div class="me-3 fw-bold">Sale <small class="fw-light">(less refunds)</small></div>
                    <div class="ms-auto">${{ parseFloat(order.sold_for).toFixed(2) }}</div>
                  </div>

                  <div class="d-flex">
                    <div class="me-3 fw-bold">Shipping Collected</div>
                    <div class="ms-auto">${{ parseFloat(order.shipping).toFixed(2) }}</div>
                  </div>

                  <hr>

                  <div class="d-flex" v-if="order.totalFeeBasisAmount">
                    <div class="me-3 fw-bold">Fee Basis (w/ Taxes)</div>
                    <div class="ms-auto">${{ parseFloat(order.totalFeeBasisAmount.value).toFixed(2) }}</div>
                  </div>

                  <div class="d-flex" v-if="order.totalMarketplaceFee">
                    <div class="me-3 fw-bold">Seller Fees</div>
                    <div class="ms-auto text-danger">${{ (parseFloat(order.totalMarketplaceFee.value)*-1).toFixed(2) }}</div>
                  </div>

                  <hr>

                  <div class="d-flex">
                    <div class="me-3 fw-bold">Total To Seller</div>
                    <div class="ms-auto text-success">${{ parseFloat(order.paymentSummary?.totalDueSeller?.value).toFixed(2) }}</div>
                  </div>

                  <hr>

                </div>

                <order-transactions
                    :key="version"
                    :order-id="orderId"
                    :order-status="order.order_status"
                    :platform="order.remote_store"
                    :is-system="order.is_system"
                    @net-updated="setNet"
                    @cogs-updated="setCogs"
                    @order-status-changed="refreshOrder"
                    @edit-order="emit('edit', order)"
                    class="mb-4" />

                <order-analytics :net="net" :cogs="cogs" :days="order.days_listed || 0" :minutes="order.minutes_listed" :total="order.pricingSummary['total']?.value" />

                <!-- Cancel Order Modal -->
                <div v-if="showCancelModal" class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
                  <div class="modal-dialog">
                    <div class="modal-content">
                      <div class="modal-header">
                        <h5 class="modal-title">Cancel Order</h5>
                        <button type="button" class="btn-close" @click="showCancelModal = false" :disabled="actionInProgress"></button>
                      </div>
                      <div class="modal-body">
                        <div v-if="actionError" class="alert alert-danger">{{ actionError }}</div>
                        <p>Are you sure you want to cancel this order?</p>
                        <div class="alert alert-warning">
                          <strong>This will:</strong>
                          <ul class="mb-0 mt-2">
                            <li>Update order status to "Cancelled"</li>
                            <li>Restore inventory quantities</li>
                            <li>Reverse COGS transactions</li>
                          </ul>
                        </div>
                        <p class="text-muted small mb-0">Order ID: <strong>{{ orderId }}</strong></p>
                      </div>
                      <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="showCancelModal = false" :disabled="actionInProgress">
                          Close
                        </button>
                        <button type="button" class="btn btn-warning" @click="handleCancelOrder" :disabled="actionInProgress">
                          <span v-if="actionInProgress" class="spinner-border spinner-border-sm me-2"></span>
                          {{ actionInProgress ? 'Cancelling...' : 'Confirm Cancellation' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Refund Order Modal -->
                <div v-if="showReturnModal" class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
                  <div class="modal-dialog">
                    <div class="modal-content">
                      <div class="modal-header">
                        <h5 class="modal-title">Refund Order</h5>
                        <button type="button" class="btn-close" @click="closeReturnModal" :disabled="actionInProgress"></button>
                      </div>
                      <div class="modal-body">
                        <div v-if="actionError" class="alert alert-danger">{{ actionError }}</div>

                        <div class="mb-3">
                          <div class="fw-semibold small mb-1">Refund Type</div>
                          <div class="form-check">
                            <input class="form-check-input" type="radio" id="refund-full" value="full" v-model="returnForm.refundType">
                            <label class="form-check-label" for="refund-full">
                              Full Refund
                              <span class="text-muted ms-1">- reverses sale, COGS, and restores inventory</span>
                            </label>
                          </div>
                          <div class="form-check">
                            <input class="form-check-input" type="radio" id="refund-partial" value="partial" v-model="returnForm.refundType">
                            <label class="form-check-label" for="refund-partial">
                              Partial Refund
                              <span class="text-muted ms-1">- reverses sale but keeps inventory sold</span>
                            </label>
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Reason</label>
                          <textarea v-model="returnForm.reason" class="form-control" rows="2" placeholder="e.g., Item not as described, buyer remorse, damaged in shipping..."></textarea>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Restocking Fee</label>
                          <div class="input-group">
                            <span class="input-group-text">$</span>
                            <input type="number" v-model.number="returnForm.restockingFee" class="form-control" min="0" step="0.01" placeholder="0.00">
                          </div>
                          <small class="text-muted">Amount deducted from refund (you keep this)</small>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Return Shipping Cost</label>
                          <div class="input-group">
                            <span class="input-group-text">$</span>
                            <input type="number" v-model.number="returnForm.returnShipping" class="form-control" min="0" step="0.01" placeholder="0.00">
                          </div>
                          <small class="text-muted">Shipping cost to receive item back</small>
                        </div>

                        <div class="alert alert-light border small mb-0">
                          <strong>What happens:</strong>
                          <ul class="mb-0 mt-1">
                            <li>Order status set to "Returned"</li>
                            <li>Sale and fee transactions reversed</li>
                            <li v-if="returnForm.refundType === 'full'">Inventory quantities restored</li>
                            <li v-if="returnForm.refundType === 'full'">COGS reversed</li>
                            <li v-if="returnForm.restockingFee > 0">Restocking fee of ${{ returnForm.restockingFee.toFixed(2) }} recorded</li>
                            <li v-if="returnForm.returnShipping > 0">Return shipping of ${{ returnForm.returnShipping.toFixed(2) }} recorded</li>
                          </ul>
                        </div>
                      </div>
                      <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeReturnModal" :disabled="actionInProgress">
                          Cancel
                        </button>
                        <button type="button" class="btn btn-info" @click="handleProcessReturn" :disabled="actionInProgress">
                          <span v-if="actionInProgress" class="spinner-border spinner-border-sm me-2"></span>
                          {{ actionInProgress ? 'Processing Refund...' : 'Confirm Refund' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Hide Order Modal -->
                <div v-if="showHideModal" class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
                  <div class="modal-dialog">
                    <div class="modal-content">
                      <div class="modal-header">
                        <h5 class="modal-title">{{ order.is_hidden ? 'Unhide Order' : 'Hide Order' }}</h5>
                        <button type="button" class="btn-close" @click="showHideModal = false" :disabled="actionInProgress"></button>
                      </div>
                      <div class="modal-body">
                        <div v-if="actionError" class="alert alert-danger">{{ actionError }}</div>
                        <p v-if="!order.is_hidden">
                          Are you sure you want to hide this order? It will be excluded from all reports and insights.
                        </p>
                        <p v-else>
                          This will make the order visible again in reports and insights.
                        </p>
                        <p class="text-muted small mb-0">Order ID: <strong>{{ orderId }}</strong></p>
                      </div>
                      <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="showHideModal = false" :disabled="actionInProgress">
                          Cancel
                        </button>
                        <button type="button" class="btn btn-warning" @click="handleSetHidden" :disabled="actionInProgress">
                          <span v-if="actionInProgress" class="spinner-border spinner-border-sm me-2"></span>
                          {{ actionInProgress ? 'Processing...' : (order.is_hidden ? 'Unhide Order' : 'Confirm Hide') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>


          </div>
        </div>

        <pre class="d-none">{{ order }}</pre>
      </div>
    </transition>
  </div>
</template>

<script setup>
import {ref, defineProps, defineEmits, nextTick} from "vue";
  import {useStore} from "vuex";

  import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
  import LocalLineItem from "@/app/components/orders/LocalLineItem.vue";
  import OrderTransactions from "@/app/components/orders/OrderTransactions.vue";
  import OrderAnalytics from "@/app/components/orders/OrderAnalytics.vue";
  import PlatformIcon from "@/app/components/Utils/PlatformIcon.vue";
  import OrderGallery from "@/app/components/orders/OrderGallery.vue";
  import CrossListingGate from "@/app/components/Utils/CrossListingGate.vue";

  const props = defineProps(['orderId', 'discreet'])
  const emit = defineEmits(['hidden', 'edit', 'order-changed'])
  const store = useStore()
  const order = ref(null)
  const error = ref(null)
  const net = ref(0)
  const cogs = ref(0)
  const version = ref(1)

  const fetching = ref(false)

  // Cancel/Return modal state
  const showCancelModal = ref(false)
  const showReturnModal = ref(false)
  const showHideModal = ref(false)
  const actionInProgress = ref(false)
  const actionError = ref(null)
  const returnForm = ref({
    refundType: 'full',
    reason: '',
    restockingFee: 0,
    returnShipping: 0
  })

  function setNet(val) {
    net.value = val
  }

  function setCogs(val) {
    cogs.value = val
  }

  getOrder()

  function getOrder() {
    if (!props.orderId) {
      error.value = 'No Order ID'
      return
    }
    fetching.value = true
    store.dispatch('eshop/getOrderDetails', { orderId: props.orderId})
        .then(res => {
          order.value = res
          fetching.value = false
        })
        .catch(err => {
          error.value = err
          fetching.value = false
        })
  }

  function refreshOrder() {
    store.dispatch('eshop/getOrderDetails', { orderId: props.orderId })
      .then(res => {
        order.value = res
        nextTick(() => {
          version.value = version.value + 1
        })
      })
  }

  async function handleCancelOrder() {
    actionInProgress.value = true
    actionError.value = null

    try {
      await store.dispatch('eshop/cancelOrder', { orderId: props.orderId })

      // Reload order to show updated status
      await getOrder()

      // Close modal
      showCancelModal.value = false
      emit('order-changed')
    } catch (err) {
      actionError.value = err.response?.data?.error || err.message || 'Failed to cancel order'
    } finally {
      actionInProgress.value = false
    }
  }

  async function handleProcessReturn() {
    actionInProgress.value = true
    actionError.value = null

    try {
      await store.dispatch('eshop/processReturn', {
        orderId: props.orderId,
        returnData: {
          refundType: returnForm.value.refundType,
          reason: returnForm.value.reason,
          restockingFee: returnForm.value.restockingFee,
          returnShipping: returnForm.value.returnShipping
        }
      })

      // Reload order to show updated status
      await getOrder()

      // Close modal and reset form
      closeReturnModal()
      emit('order-changed')
    } catch (err) {
      actionError.value = err.response?.data?.error || err.message || 'Failed to process return'
    } finally {
      actionInProgress.value = false
    }
  }

  async function handleUndoCancel() {
    actionInProgress.value = true
    actionError.value = null

    try {
      await store.dispatch('eshop/undoCancelOrder', { orderId: props.orderId })
      await getOrder()
      emit('order-changed')
    } catch (err) {
      actionError.value = err.response?.data?.error || err.message || 'Failed to undo cancellation'
    } finally {
      actionInProgress.value = false
    }
  }

  async function handleSetHidden() {
    actionInProgress.value = true
    actionError.value = null

    try {
      const newHiddenState = order.value.is_hidden ? 0 : 1
      await store.dispatch('eshop/setOrderHidden', {
        orderId: props.orderId,
        isHidden: newHiddenState
      })

      showHideModal.value = false

      if (newHiddenState === 1) {
        // After hiding, emit event so parent can handle (e.g., close modal, refresh list)
        emit('hidden', { orderId: props.orderId, is_hidden: newHiddenState })
      } else {
        // After unhiding, reload order and refresh list
        await getOrder()
        emit('order-changed')
      }
    } catch (err) {
      actionError.value = err.response?.data?.error || err.message || 'Failed to update order visibility'
    } finally {
      actionInProgress.value = false
    }
  }

  function closeReturnModal() {
    showReturnModal.value = false
    actionError.value = null
    // Reset form
    returnForm.value = {
      refundType: 'full',
      reason: '',
      restockingFee: 0,
      returnShipping: 0
    }
  }

  function updateLineItem(index, updatedItem) {
    if (order.value?.lineItems?.[index]) {
      order.value = { ...order.value, lineItems: order.value.lineItems.map((item, i) => i === index ? { ...item, ...updatedItem } : item) }
      emit('order-changed', true)
    }
  }
</script>

<style lang="scss" scoped>
.right-column {width : 200px;}
.order-container { max-width: 1100px; margin:0 auto;}

</style>
